/**
 * Resolve the effective Weekly Behaviour settings for a given
 * tab + pure-math helpers for the session-day / session-week
 * indexing the chart bands use.
 *
 * Scope semantics (DIFFERS from theme / fib): Weekly Behaviour is
 * per-chart only. The User scope is just a saved TEMPLATE used to
 * seed `Customize` on a new chart — it doesn't cascade into the
 * render. So a User-scope edit never silently changes existing
 * charts; charts only render bands when the tab has its OWN
 * workspace-scope settings.
 *
 * Returns null when the active chart hasn't been customized; the
 * Chart renderer interprets that as "no bands".
 */

import type { RootState } from "../store";
import type { WeeklyBehaviourSettings } from "../slices/uiSlice";

export function selectEffectiveWeeklyBehaviour(
  state: RootState,
  tabId: string,
): WeeklyBehaviourSettings | null {
  return state.ui.byTab[tabId]?.weeklyBehaviour ?? null;
}

// ---- Data-driven session boundaries ----
//
// Exness shifts the actual session pause by 1-2 hours twice a year
// for DST, so a fixed UTC-rollover hour is unreliable. The
// authoritative source is the tick data: the daily pause (~1h)
// and the weekend pause (~49h) leave easily-detectable gaps.
// `pulse-engine/scripts/detect_session_boundaries.py` precomputes
// them and the backend serves them at /api/session_boundaries/{symbol}.
//
// Each ParsedBoundary records:
//   closeMs    — last tick before the pause (= end of a session)
//   openMs     — first tick after the pause (= start of the next)
//   dayOfWeek  — Mon..Fri (0..4) of THIS boundary's close date
//   weekIndex  — Monday-anchored, monotonic, used for Mode B palette
//
// At chart render time we binary-search for the first boundary
// whose closeMs is at-or-after a candle's time; that boundary's
// dayOfWeek / weekIndex identify which session-day / session-week
// the candle belongs to.

import type { SessionBoundary } from "@/api/types";

const MS_IN_DAY = 24 * 3600 * 1000;
const MS_IN_WEEK = 7 * MS_IN_DAY;

export interface ParsedBoundary {
  closeMs: number;
  openMs: number;
  dayOfWeek: number;
  weekIndex: number;
}

// Monday 2020-01-06 at 00:00 UTC — used as the weekIndex anchor.
// Picked so the index stays positive for all live trade data.
const JAN6_2020_UTC_MS = Date.UTC(2020, 0, 6, 0, 0, 0);

export function parseSessionBoundary(b: SessionBoundary): ParsedBoundary {
  const closeMs = Date.parse(b.session_close_utc);
  const openMs = Date.parse(b.next_session_open_utc);
  const closeDate = new Date(closeMs);
  // Mon=0..Sun=6 (instead of JS's Sun=0..Sat=6).
  const mondayDow = (closeDate.getUTCDay() + 6) % 7;
  const dayOfWeek = mondayDow > 4 ? -1 : mondayDow;
  // Monday-of-this-close-date at 00:00 UTC.
  const dateMidnight = Date.UTC(
    closeDate.getUTCFullYear(),
    closeDate.getUTCMonth(),
    closeDate.getUTCDate(),
  );
  const mondayMs = dateMidnight - mondayDow * MS_IN_DAY;
  const weekIndex = Math.floor(
    (mondayMs - JAN6_2020_UTC_MS) / MS_IN_WEEK,
  );
  return { closeMs, openMs, dayOfWeek, weekIndex };
}

/** A single forex session-day is ~24h; the longest legitimate span
 *  from a candle to its own session-close is the Sunday-evening open
 *  (~22:00 UTC) to the Monday close (~21:00 UTC), ≈23h. Give it a 3h
 *  margin: if the nearest boundary's close is more than this far ahead
 *  of the candle, the candle's OWN boundary is missing (holiday
 *  early-close, download gap, partial reconciliation) and we must not
 *  inherit a far-future boundary's day-of-week. */
const MAX_SESSION_LOOKAHEAD_MS = 26 * 60 * 60 * 1000;

/** Binary-search for the first boundary whose `closeMs` is at or
 *  after `timeMs`. Returns null when `timeMs` is past every known
 *  boundary (the still-open live session) OR when the nearest boundary
 *  is implausibly far ahead (a gap in the cache — see
 *  MAX_SESSION_LOOKAHEAD_MS). Callers fall back to the calendar
 *  rollover heuristic in both cases. */
function lookupBoundary(
  timeMs: number,
  boundaries: ParsedBoundary[],
): ParsedBoundary | null {
  let lo = 0;
  let hi = boundaries.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (boundaries[mid].closeMs < timeMs) lo = mid + 1;
    else hi = mid;
  }
  if (lo >= boundaries.length) return null;
  const b = boundaries[lo];
  if (b.closeMs - timeMs > MAX_SESSION_LOOKAHEAD_MS) return null;
  return b;
}

/** Median UTC hour of the last ~10 boundaries' `openMs`. That's
 *  the broker's current daily-rollover hour, DST-aware
 *  automatically because it's derived from observed data.
 *  Returns null if we have no boundaries to derive from. */
export function deriveRolloverHour(
  boundaries: ParsedBoundary[],
): number | null {
  if (boundaries.length === 0) return null;
  // Last 10 boundaries are enough to vote out outliers (holiday
  // shifts, the weekend boundary's slightly-different open hour)
  // while staying current with DST.
  const lastN = boundaries.slice(-10);
  const hours = lastN.map((b) => new Date(b.openMs).getUTCHours());
  hours.sort((a, b) => a - b);
  return hours[Math.floor(hours.length / 2)];
}

/** When the candle is past every known boundary it's in the live
 *  session. We classify it using a rollover-hour heuristic derived
 *  from the LAST FEW boundaries — the heuristic naturally handles
 *  the weekend wrap-around (Friday-evening UTC → next-Monday
 *  session) which a "project +24h from last open" approach drifts
 *  through after multiple un-reconciled days.
 *
 *  Returns null only when there are no boundaries to derive a
 *  rollover from. */
function extrapolateLiveSession(
  timeMs: number,
  boundaries: ParsedBoundary[],
): { dayOfWeek: number; weekIndex: number } | null {
  const rolloverHour = deriveRolloverHour(boundaries);
  if (rolloverHour === null) return null;
  // HTF-bar fix: a 4H Sun-20-UTC bar or 1D Sun-00-UTC bar has its
  // timestamp in Sunday-pre-rollover (= weekend by calendar), but
  // every tick it contains belongs to the Monday session that
  // opens during the bar window at the rollover hour. The
  // boundary-lookup path handles this correctly because it jumps
  // to the next-upcoming Monday-close boundary; the live extrapolation
  // doesn't, unless we shift the effective time forward to the
  // rollover. Only applies on Sundays pre-rollover and only when
  // the rollover is within ~24h (= the bar likely contains it).
  let effectiveMs = timeMs;
  const d0 = new Date(timeMs);
  if (d0.getUTCDay() === 0 && d0.getUTCHours() < rolloverHour) {
    const reopenMs = Date.UTC(
      d0.getUTCFullYear(),
      d0.getUTCMonth(),
      d0.getUTCDate(),
      rolloverHour,
    );
    if (reopenMs - timeMs <= MS_IN_DAY) {
      effectiveMs = reopenMs;
    }
  }
  const d = new Date(effectiveMs);
  const utcHour = d.getUTCHours();
  // Calendar day-of-week with Mon=0..Sun=6.
  const calendarDow = (d.getUTCDay() + 6) % 7;
  // Past the rollover hour → the candle is in TOMORROW's session.
  const sessionDow =
    utcHour >= rolloverHour ? (calendarDow + 1) % 7 : calendarDow;
  // Mon..Fri = 0..4 are tradable; Sat/Sun → -1 (no band).
  const dayOfWeek = sessionDow > 4 ? -1 : sessionDow;
  // Week index uses the SESSION date (today if pre-rollover,
  // tomorrow if post), then back up to that date's Monday.
  let sessionDateMs = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
  );
  if (utcHour >= rolloverHour) sessionDateMs += MS_IN_DAY;
  const sessionDate = new Date(sessionDateMs);
  const sessionDateDow = (sessionDate.getUTCDay() + 6) % 7;
  const mondayMs = sessionDateMs - sessionDateDow * MS_IN_DAY;
  const weekIndex = Math.floor(
    (mondayMs - JAN6_2020_UTC_MS) / MS_IN_WEEK,
  );
  return { dayOfWeek, weekIndex };
}

/** Session day-of-week for a candle at `timeMs` (0=Mon..4=Fri, -1
 *  outside Mon-Fri sessions). Past every known boundary, projects
 *  from the last open's 24h-spaced sessions; with no boundaries at
 *  all, falls back to the bare UTC weekday. */
export function sessionDayFromBoundaries(
  timeMs: number,
  boundaries: ParsedBoundary[],
): number {
  const b = lookupBoundary(timeMs, boundaries);
  if (b !== null) return b.dayOfWeek;
  const ex = extrapolateLiveSession(timeMs, boundaries);
  if (ex !== null) return ex.dayOfWeek;
  // Bare-bones fallback when we have no boundary data at all.
  const d = new Date(timeMs);
  const dow = (d.getUTCDay() + 6) % 7;
  return dow > 4 ? -1 : dow;
}

/** Session week index (Monday-aligned) for a candle. Used by
 *  Mode B palette rotation. Same projection / fallback chain as
 *  `sessionDayFromBoundaries`. */
export function sessionWeekFromBoundaries(
  timeMs: number,
  boundaries: ParsedBoundary[],
): number {
  const b = lookupBoundary(timeMs, boundaries);
  if (b !== null) return b.weekIndex;
  const ex = extrapolateLiveSession(timeMs, boundaries);
  if (ex !== null) return ex.weekIndex;
  const d = new Date(timeMs);
  const mondayDow = (d.getUTCDay() + 6) % 7;
  const dateMidnight = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
  );
  const mondayMs = dateMidnight - mondayDow * MS_IN_DAY;
  return Math.floor((mondayMs - JAN6_2020_UTC_MS) / MS_IN_WEEK);
}
